import React, {
    useState,
    useEffect,
    useContext,
    useMemo,
    useCallback,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { IconButton, Divider, Box } from '@mui/material'
import {
    DialogPopup,
    Pagination,
    SearchFilter,
    formatDate,
    getOffset,
    usePagination,
    useSearchParams,
    KeyPairsIcon,
    MoreVertIcon,
    AddIcon,
} from '../components'
import { getKeyPairs, deleteKeyPair } from '../api'
import { KeyPair, KeyPairListParams, KeyPairs } from '../model'
import { AppContext } from '../context'
import { translate } from '../i18n'
import KeyPairMenu from './KeyPairMenu'
import { Griddable } from '../grid'
import { FeedbackContext } from '../feedback'
import KeyPairDetails from './KeyPairDetails'

export default function KeyPairsList() {
    const history = useHistory()
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)
    const tenantId = context.session?.tenant?.id ?? '-'

    const allowedCreate = useMemo(
        () => context.isGranted('KeyPairsCreate'),
        [context]
    )

    const query = new URLSearchParams(useLocation().search)
    const params = useSearchParams<KeyPairListParams>(query)

    const [status, setStatus] = useState<'loading' | 'loaded' | Error>(
        'loading'
    )
    const [data, setData] = useState<KeyPairs>()

    const [keyPair, setKeyPair] = useState<KeyPair>()
    const [popup, setPopup] = useState<'details' | 'delete'>()

    const clearCallback = useCallback(() => setData(undefined), [])
    const { page, pageSize, onChangedPage, onChangedPageSize } =
        usePagination(clearCallback)

    const retrievePromise = useCallback((): Promise<KeyPairs> => {
        const offset = getOffset(page, pageSize)
        return getKeyPairs(tenantId, { ...params, pageSize, offset })
    }, [tenantId, page, pageSize, params])

    const reload = useCallback(() => {
        setData(undefined)
        setStatus('loading')
        retrievePromise()
            .then((response) => {
                setData(response)
                setStatus('loaded')
            })
            .catch((error) => {
                setStatus(error)
            })
    }, [retrievePromise])

    useEffect(reload, [reload])

    const onDetails = (keyPair: KeyPair) => {
        setKeyPair(keyPair)
        setPopup('details')
    }

    const onDelete = (keyPair: KeyPair) => {
        setKeyPair(keyPair)
        setPopup('delete')
    }

    const onClosePopup = useCallback(() => {
        setPopup(undefined)
    }, [])

    const onConfirmDelete = useCallback(() => {
        if (!keyPair) return

        onClosePopup()

        feedbackContext.showBackdrop(
            translate('keypairs.delete.processing', {
                name: keyPair.name,
            }) as string
        )

        deleteKeyPair(tenantId, keyPair.id)
            .then(() => {
                feedbackContext.showSuccess(
                    translate('keypairs.delete.success', keyPair) as string
                )
                reload()
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }, [tenantId, keyPair, reload, onClosePopup, feedbackContext])

    const onClicked = useCallback(
        (keyPair: KeyPair) => {
            history.push(`/keypairs/${keyPair.id}/edit`)
        },
        [history]
    )

    return (
        <Pagination
            title={translate('keypairs.title')}
            icon={<KeyPairsIcon />}
            page={page}
            pageSize={pageSize}
            count={data ? data.items.length : 0}
            total={data ? data.total : 0}
            onChangedPage={onChangedPage}
            onChangedPageSize={onChangedPageSize}
            buttons={[
                {
                    text: translate('buttons.add'),
                    icon: <AddIcon />,
                    path: '/keypairs/new',
                    available: allowedCreate,
                    variant: 'primary',
                },
            ]}
        >
            <SearchFilter
                label={translate('keypairs.filter') as string}
                query={query}
            />
            <Box mx={-2} mt={2}>
                <Divider />
                <Griddable
                    items={data ? data.items : []}
                    loading={status === 'loading'}
                    error={status instanceof Error ? status.message : undefined}
                    empty={translate('keypairs.empty') as string}
                    getId={(keyPair: KeyPair) => keyPair.id}
                    clickable={{
                        onClick: onClicked,
                    }}
                    refreshable={{
                        columnId: 'actions',
                        onClick: reload,
                    }}
                    columns={[
                        {
                            title: translate('keypairs.name') as string,
                            converter: (keyPair: KeyPair) => keyPair.name,
                            id: 'name',
                            fixed: 'left',
                        },
                        {
                            title: translate('keypairs.id') as string,
                            converter: (keyPair: KeyPair) => keyPair.id,
                            id: 'id',
                        },
                        {
                            title: translate('keypairs.secret') as string,
                            converter: (keyPair: KeyPair) =>
                                `${keyPair.secret.substring(
                                    0,
                                    4
                                )}********${keyPair.secret.substring(44, 48)}`,
                            id: 'secret',
                        },
                        {
                            title: translate('keypairs.created_at') as string,
                            converter: (keyPair: KeyPair) =>
                                formatDate({ date: keyPair.created_at }),
                            id: 'created_at',
                        },
                        {
                            title: (
                                <IconButton
                                    size="small"
                                    style={{ visibility: 'hidden' }}
                                    disabled
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            ),
                            converter: (keyPair: KeyPair) => (
                                <KeyPairMenu
                                    keyPair={keyPair}
                                    onDetails={onDetails}
                                    onDelete={onDelete}
                                />
                            ),
                            id: 'actions',
                            fixed: 'right',
                            width: 60,
                            textAlign: 'center',
                        },
                    ]}
                />
            </Box>

            {keyPair && popup === 'details' && (
                <KeyPairDetails
                    tenantId={tenantId}
                    keyPair={keyPair}
                    onClose={onClosePopup}
                />
            )}
            {keyPair && popup === 'delete' && (
                <DialogPopup
                    open
                    variant="error"
                    title={translate('keypairs.delete.title')}
                    onClose={onClosePopup}
                    button={{
                        text: translate('buttons.delete'),
                        onClick: onConfirmDelete,
                    }}
                >
                    {translate('keypairs.delete.text', keyPair)}
                </DialogPopup>
            )}
        </Pagination>
    )
}
